import axios from 'axios';
import { api } from '../../stores/api';
import { z } from 'zod';
import { _ } from 'svelte-i18n';
import { getJobPin } from '../components/Jobs/JobsOnMap';
import type { DetailsRoutePointType, RoutePointSimpleType } from './RoutePointsApi';
import type { RealisationStatusType } from '../components/Planning/AssignmentApi';
import { deliveryList } from '../../stores/store';
import { get } from 'svelte/store';
import { ApiFilterBuilder } from '@automapa/svelte-automapa-template';
import { BalloonContentGenerator } from '../components/Jobs/BalloonContentGenerator';
import { Job } from '../components/Jobs/Job';
import { ResolutionTime } from '../components/telematics/components/Colorizer/ResolutionTime';
// import { stringify } from 'flatted';

export class MapRoute extends EventTarget {
    private assignmentId: number;
    private routeId: string;
    private baseRouteId: string | null = null;
    private points: Array<any> = [];
    private map: any;
    private route: any = null;
    private baseRoute: any = null;
    private loadedArea: any = null;
    private recentZoom: number | null = null;
    private pointRecentlyCompleted: number | null = null;
    private routeStyleBefore: any = {
        done: [
            { strokeWeight: '7px', strokeColor: '#333333', strokeOpacity: '0.7', lineCap: 'butt' },
            { strokeWeight: '5px', strokeColor: '#bababa', strokeOpacity: '1', lineCap: 'butt' },
        ],
        current: [
            { strokeWeight: '7px', strokeColor: '#333333', strokeOpacity: '0.7', lineCap: 'butt' },
            { strokeWeight: '5px', strokeColor: '#5f9cfd', strokeOpacity: '1', lineCap: 'butt' },
        ],
        future: [
            { strokeWeight: '7px', strokeColor: '#333333', strokeOpacity: '0.7', lineCap: 'butt' },
            { strokeWeight: '5px', strokeColor: '#ffffff', strokeOpacity: '1', lineCap: 'butt' },
        ],
    };
    private routeStyleWithBorders: any = {
        done: [
            { strokeWeight: '7px', strokeColor: '#333333', strokeOpacity: '0.7', lineCap: 'butt' },
            { strokeWeight: '5px', strokeColor: '#a4d86a', strokeOpacity: '1', lineCap: 'butt' },
        ],
        current: [
            { strokeWeight: '7px', strokeColor: '#333333', strokeOpacity: '0.7', lineCap: 'butt' },
            { strokeWeight: '5px', strokeColor: '#1c5dc7', strokeOpacity: '1', lineCap: 'butt' },
        ],
        future: [
            { strokeWeight: '7px', strokeColor: '#333333', strokeOpacity: '0.7', lineCap: 'butt' },
            { strokeWeight: '5px', strokeColor: '#5f9cfd', strokeOpacity: '1', lineCap: 'butt' },
        ],
        base: [
            { strokeWeight: '7px', strokeColor: '#333333', strokeOpacity: '0.7', lineCap: 'butt' },
            { strokeWeight: '5px', strokeColor: '#ffffff', strokeOpacity: '1', lineCap: 'butt' },
        ],
    };
    private routeStyle: any = {
        done: [
            { strokeWeight: '7px', strokeColor: '#999999', strokeOpacity: '1', lineCap: 'butt' },
        ],
        current: [
            { strokeWeight: '7px', strokeColor: '#1c5dc7', strokeOpacity: '1', lineCap: 'butt' },
        ],
        future: [
            { strokeWeight: '7px', strokeColor: '#5f9cfd', strokeOpacity: '1', lineCap: 'butt' },
        ],
        base: [
            { strokeWeight: '7px', strokeColor: '#ffffff', strokeOpacity: '1', lineCap: 'butt' },
        ],
    };
    private api: any;
    private apiController: any;
    private currentPointsVisibility: boolean[] = [];

    constructor(assignmentId: number, routeId: string, points: Array<any>) {
        super();
        /** @ts-ignore */
        this.map = Targeo.Base.Map;
        this.api = null;
        this.apiController = api.subscribe(
            function (this: MapRoute, v: any) {
                this.api = v;
            }.bind(this)
        );
        this.assignmentId = assignmentId;
        this.routeId = routeId;
        this.map.getObjectManager().setClusterStyle('lastmileRoutePoint', {
            color: '#000',
            borderColor: '#000',
            backgroundColor: '#fff',
            offset: { x: 9, y: -9 },
        });
        this.map.listen('onMoveEnd', this.routeFetch, false, this);
        this.map.listen('onZoomChange', this.routeFetch, false, this);
        this.pointsAdd(points);
        this.routeGetData();
    }

    pointsAdd(points: any[]) {
        if (this.currentPointsVisibility.length != this.points.length) {
            this.currentPointsVisibility = [];
        }
        let recentlyCompleted = { idx: -1, ts: 0 };
        for (let i = 0, ii = points.length; i < ii; i++) {
            if (points[i].jobs || i == 0 || i == points.length - 1) {
                let p = points[i];
                let label = null;
                let c = '#ffffff';
                if (i == 0) {
                    c = '#529749';
                } else if (i == points.length - 1) {
                    c = '#db2122';
                } else {
                    label = { text: p.id ? i.toString() : 'M', size: 10, color: '#333333', offset: { x: 0, y: 0 } };
                    c = this.pointGetColor(p);
                    if (
                        recentlyCompleted &&
                        p.details &&
                        p.details.completed &&
                        p.details.completed > recentlyCompleted.ts
                    ) {
                        recentlyCompleted = { idx: i, ts: p.details.completed };
                    }
                }
                if (i >= this.currentPointsVisibility.length) {
                    this.currentPointsVisibility.push(true);
                }
                p.mapObject = new Targeo.MapObject.Oval(
                    { x: parseFloat(p.geom.coordinates[0]), y: parseFloat(p.geom.coordinates[1]) },
                    10,
                    p.id ? 'routepoint_'+p.id : null,
                    {
                        visible: this.currentPointsVisibility[i],
                        zIndex: 210 + (i % points.length) / 1000,
                        strokeWeight: 2,
                        strokeColor: '#333333',
                        fillColor: c,
                        group:
                            p.jobs && p.jobs.length > 1
                                ? { size: p.jobs.length, cluster: 'lastmileRoutePoint' }
                                : false,
                        label: label,
                        balloon: {
                            width: 410,
                            body: '<div class="lds-dual-ring-improved" style="width: 60px; height: 60px;"></div>',
                        },
                        // add onclick event
                        mouseButtonActions: {
                            left: function (mapObj: any) {
                                // let jobId = parseInt(mapObj.id.slice(4));
                                console.log('JobList, point on map clicked (MapRoute)', mapObj, p);
                                if (p.jobs) {
                                    deliveryList.set({ ...get(deliveryList), expandedIndex: p.jobs });
                                }
                            },
                        },
                    }
                );
                p.jobsDataLoaded = false;
                p.mapObject.listen(
                    'onBalloonShow',
                    function (this: MapRoute, ev: any) {
                        let pointId = ev.target.id;
                        let point = this.points.filter((p) => p && p.mapObject.id === pointId)[0] || null;
                        if (point) {
                            if (1 || !point.jobsDataLoaded) {
                                let filter = new ApiFilterBuilder(-1);
                                filter.setRelations(['assignment_job', 'assignment_job_rejection', 'assignment']);
                                filter.setProjectionFields(['job.*', 'geom', 'assignment_job>details@realisation', 'assignment_job_rejection>driver_info@rejection', 'assignment.start_time_route']);
                                let queries = {
                                    id: {$in: point.jobs},
                                    $or: [
                                        {rejected: false},
                                        //{'rejected_assignments.-1::int': {$column: 'assignment_job_rejection.assignment_id'}}
                                        {'assignment_job_rejection.assignment_id': this.assignmentId}
                                    ]
                                };
                                this.api
                                    .getData('post', '/jobs/query'+filter.getUri(), queries, z.any(), z.any())
                                    .then(
                                        function (this: MapRoute, jobs: any[]) {
                                            let point = this.points.filter((p) => p && p.mapObject.id === pointId)[0] || null;
                                            if (jobs?.length && point) {
                                                let html = [];
                                                let parcels = 0;
                                                jobs.forEach((j) => { parcels += (parseInt(j.special_attributes.parcels) || 0); });
                                                html.push('<div style="display: flex; flex-direction: row; margin-bottom: 10px">');
                                                html.push('<div style="width: 50%; text-align: center"><label style="opacity:0.5">'+get(_)('Route_Point_Balloon_Jobs')+':</label> <span style="font-weight: bold; font-size: 150%">'+jobs.length+'</span></div>');
                                                if (parcels) {
                                                    html.push('<div style="width: 50%; text-align: center"><label style="opacity:0.5">'+get(_)('Route_Point_Balloon_Parcels')+':</label> <span style="font-weight: bold; font-size: 150%">'+parcels+'</span></div>');
                                                }
                                                html.push('</div>');
                                                let realisation = point.details || {};
                                                let etaTimestamp = null;
                                                if (jobs[0].assignment?.start_time_route) {
                                                    let routeStartTimestamp = Date.parse(jobs[0].assignment?.start_time_route.replace(' ', 'T'));
                                                    etaTimestamp = routeStartTimestamp+point.delta.g.t*60*1000;
                                                    if (routeStartTimestamp && typeof(point.delta.g.t) == 'number') {
                                                        html.push('<div><label style="opacity:0.5;">' + get(_)('Route_Point_Balloon_ETA') + ':</label> ' + ResolutionTime.formatTimeFromDate(new Date(etaTimestamp)) + '</div>')
                                                    }
                                                }
                                                if (realisation.approached) {
                                                    let approached = new Date(realisation.approached*1000);
                                                    html.push(
                                                        '<div><label style="opacity:0.5;">'
                                                        + get(_)('Route_Point_Balloon_Approached')
                                                        + ':</label> '
                                                        + '<b>'+ResolutionTime.formatTimeFromDate(approached)+'</b>'
                                                        + (etaTimestamp && Math.abs(etaTimestamp-realisation.approached*1000) >= 300000 ? ' (<b>' + ResolutionTime.formatDurationFromMinutes(Math.floor(Math.abs(etaTimestamp-realisation.approached*1000)/60000)).replace(/([h+-])/g, '</b><span style="opacity: 0.5">$1</span><b>') + '</b>)' : '')
                                                        + '</div>'
                                                    );
                                                }
                                                if (realisation.completed) {
                                                    let completed = new Date(realisation.completed*1000);
                                                    html.push('<div><label style="opacity:0.5;">' + get(_)('Route_Point_Balloon_Completed') + ':</label> <b>' + ResolutionTime.formatTimeFromDate(completed) + '</b></div>')
                                                }
                                                let generators = [];
                                                for (let i = 0; i < jobs.length; i++) {
                                                    let balloonContentGenerator = new BalloonContentGenerator(
                                                        new Job().set(jobs[i])
                                                    );
                                                    html.push('<div style="margin-top: 10px; padding-top: 10px; border-top: 1px solid #c4c4c4">');
                                                    html.push(...balloonContentGenerator.getBalloonHtmlArray());
                                                    html.push('</div>');
                                                    generators.push(balloonContentGenerator);
                                                }
                                                point.mapObject.setBalloonOptions({
                                                    body: html.join(''),
                                                });
                                                this.map.getObjectManager().refreshBalloon(pointId);
                                                for (let i = 0; i < jobs.length; i++) {
                                                    generators[i].connectBalloonThumbnails();
                                                }
                                                point.jobsDataLoaded = true;
                                            }
                                        }.bind(this)
                                    )
                                    .catch();
                            } else {
                                let balloonContentGenerator = new BalloonContentGenerator(
                                    new Job().set(PlanningOnMap.jobPins[job.id].detailsLoaded)
                                );
                                balloonContentGenerator.connectBalloonThumbnails();
                            }
                        }
                    }.bind(this)
                );
                this.map.UOAdd(p.mapObject);
                this.points.push(p);
            } else {
                this.points.push(null);
                if (i >= this.currentPointsVisibility.length) {
                    this.currentPointsVisibility.push(true);
                }
            }
        }
        if (recentlyCompleted.ts) {
            this.pointRecentlyCompleted = recentlyCompleted.idx;
        }
    }

    updateRealisationStatus(realisation: RealisationStatusType) {
        if (realisation.route_points) {
            let recentlyCompleted = { idx: -1, ts: 0 };
            for (let i = 0, ii = realisation.route_points.length; i < ii; i++) {
                let point = this.points.find(p => p && p.id === realisation.route_points[i].id);
                if (point) {
                    point.details = realisation.route_points[i].details;
                    point.mapObject?.setStyle({
                        fillColor: this.pointGetColor(point)
                    });
                    if (point.details?.completed && point.details.completed > recentlyCompleted.ts) {
                        recentlyCompleted = {idx: this.points.indexOf(point), ts: point.details.completed};
                    }
                }
            }
            if (recentlyCompleted.ts) {
                this.pointRecentlyCompleted = recentlyCompleted.idx;
            }
        }

        if (this.route) {
            for (let i = 0, ii = this.route.polylines.length; i < ii; i++) {
                this.route.polylines[i].setStyle(
                    this.getSegmentStyle(this.routeId, this.route.polylines[i].segNum[0])
                );
            }
        }
    }

    private pointGetColor(point: any): string {
        let color: string = '#ffffff';
        if (point.details) {
            if (point.details.completed) {
                color = '#999999';
            } else if (point.details.status === 'current') {
                color = '#4B76DE';
            }
        }
        return color;
    }

    pointsRemove() {
        if (this.points) {
            for (let i = 0, ii = this.points.length; i < ii; i++) {
                if (this.points[i] && this.points[i].mapObject) {
                    this.map.UORemove(this.points[i].mapObject.id);
                }
            }
        }
    }

    routeGetData() {
        let routeIds = [this.routeId, this.baseRouteId].filter(r => r);
        for (let routeId of routeIds) {
            let params: any = {
                rpc: 'FindRouteJSONSimplified',
                zoom: this.map.getZoom(),
                bounds: JSON.stringify(this.map.getVisibleExtent()),
                routeid: routeId,
                defroute: 1,
                local: false,
                cut: true,
            };
            Object.keys(params).forEach((key, index) => {
                params[key] = key + '=' + encodeURIComponent(params[key]);
            });
            params = Object.values(params).join('&');
            axios
                .get(
                    _TARGEO_GLOBALS.url +
                        '/service.html?' +
                        params +
                        '&' +
                        _TARGEO_GLOBALS.urlParams,
                    {
                        withCredentials: true,
                    }
                )
                .then(
                    ((response: any) => {
                        console.log('MapRoute, routeGetData', response.data);
                        this.routeOnFetch(response.data);
                    }).bind(this)
                );
        }
    }

    routeFetch() {
        let routes = [this.route, this.baseRoute].filter(r => r);
        let currentArea = this.map.getVisibleExtent();
        if (
            this.recentZoom != this.map.getZoom() ||
            !this.loadedArea ||
            currentArea.minX < this.loadedArea.minX ||
            currentArea.minY < this.loadedArea.minY ||
            currentArea.maxX > this.loadedArea.maxX ||
            currentArea.maxY > this.loadedArea.maxY
        ) {
            this.recentZoom = this.map.getZoom();
            for (let route of routes) {
                if (
                    currentArea.minX <= route.params.maxX &&
                    currentArea.maxX >= route.params.minX &&
                    currentArea.minY <= route.params.maxY &&
                    currentArea.maxY >= route.params.minY
                ) {
                    let params: any = {
                        rpc: 'FindRouteJSONSimplified',
                        zoom: this.map.getZoom(),
                        bounds: JSON.stringify(currentArea),
                        routeid: route.id,
                        cut: 1,
                    };
                    Object.keys(params).forEach((key, index) => {
                        params[key] = key + '=' + encodeURIComponent(params[key]);
                    });
                    params = Object.values(params).join('&');
                    axios
                        .get(
                            _TARGEO_GLOBALS.url +
                                '/service.html?' +
                                params +
                                '&' +
                                _TARGEO_GLOBALS.urlParams,
                            {
                                withCredentials: true,
                            }
                        )
                        .then(
                            ((response: any) => {
                                this.routeOnFetch(response.data);
                            }).bind(this)
                        );
                }
            }
        }
    }

    routeOnFetch(json: any) {
        // MapRoute, routeGetData
        // {
        //   "djson": "",
        //   "time": [],
        //   "desc": true,
        //   "bounds": {
        //     "minX": 20.965611910888676,
        //     "maxX": 21.095044589111325,
        //     "minY": 52.09217154279915,
        //     "maxY": 52.27257714376023
        //   },
        //   "captcha": false,
        //   "data": null
        // }
        console.log('MapRoute, routeOnFetch', json, json.routeid == this.routeId);
        if (json.routeid == this.routeId || json.routeid == this.baseRouteId) {
            var i, ii, j, k, p, zone;
            let route =
                json.routeid == this.routeId
                    ? this.route
                    : json.routeid == this.baseRouteId
                      ? this.baseRoute
                      : null;
            if (json.bounds) {
                this.loadedArea = json.bounds;
                for (i in this.loadedArea) {
                    this.loadedArea[i] = parseFloat(this.loadedArea[i]);
                }
            }
            if (route) {
                if (route.polylines) {
                    for (i = 0; i < route.polylines.length; i++) {
                        this.map.UORemove(route.polylines[i].id);
                    }
                }
                if (route.apoints) {
                    for (i = 0; i < route.apoints.length; i++) {
                        this.map.UORemove(route.apoints[i].id);
                    }
                }
            }
            if (json.params) {
                route = { id: json.routeid, type: json.params.type, params: json.params };
                for (i in route.params) {
                    if (i.match(/^(min|max)(X|Y)$/)) {
                        route.params[i] = parseFloat(route.params[i]);
                    }
                }
                if (json.routeid == this.routeId) {
                    this.route = route;
                    this.map.zoomToBounds(route.params, false, [65, 125, 25, 125]);
                } else {
                    this.baseRoute = route;
                }
            }
            if (route) {
                route.polylines = [];
                route.apoints = json.apoints;
                this.map.getObjectManager().disableRefreshing('LastMile:Route');
                if (json.data) {
                    this.recentZoom = this.map.getZoom();
                    let style;
                    let zIndex;
                    if (json.routeid == this.routeId) {
                        zIndex = 202;
                    } else {
                        zIndex = 201;
                    }
                    for (i = 0; i < json.data.length; i++) {
                        style = this.getSegmentStyle(json.routeid, i);
                        for (j = 0; j < json.data[i].length; j++) {
                            var chunks = json.data[i][j];
                            if (typeof chunks != 'object') {
                                chunks = [chunks];
                            }
                            for (k = 0; k < chunks.length; k++) {
                                p = new Targeo.MapObject.Polyline(chunks[k], {
                                    visible: this.currentPointsVisibility[i] && this.currentPointsVisibility[i+1],
                                    drawStyle: style,
                                    zIndex: zIndex,
                                });
                                p.segNum = [i, j];
                                p._style = style;
                                route.polylines.push(p);
                                this.map.UOAdd(p);
                            }
                        }
                    }
                }
                this.map.getObjectManager().enableRefreshing('LastMile:Route');
                if (!json.params) {
                    this.routeFetch();
                }
            }
        }
    }

    private getSegmentStyle(routeid: string, idx: number) {
        let style;
        if (routeid == this.routeId) {
            style = this.routeStyle.future;
            if (idx == this.pointRecentlyCompleted) {
                style = this.routeStyle.current;
            }
            let next = 1;
            if (!this.points[idx + next]) {
                next++;
            }
            if (this.points[idx + next]?.details) {
                if (
                    this.points[idx + next].details.completed ||
                    this.points[idx + next].details.approached
                ) {
                    style = this.routeStyle.done;
                }
            }
        } else {
            style = this.routeStyle.base;
        }
        return style;
    }

    public changePointsOrder(order: (number|null)[] | null, deltas: ({ t: number; d: number; g: { t: number; d: number; m: number } } | null)[] | null) {
        let newPoints = [];
        let removed = 0;
        if (order === null) {
            order = [];
            for (let i = 0; i < this.points.length; i++) {
                if (this.points[i]) {
                    order.push(i);
                } else {
                    order.push(-1);
                }
            }
        }
        for (let i = 0; i < order.length; i++) {
            if (order[i] !== null) {
                let point;
                if (order[i] >= 0) {
                    point = this.points[order[i]];
                    if (point.point_order != (i - removed) && point.jobs) {
                        point.mapObject.setLabel({
                            text: (i - removed).toString(),
                            size: 10,
                            color: '#333333',
                            offset: { x: 0, y: 0 },
                        });
                    }
                    point.point_order = i - removed;
                    if (deltas && deltas[i]) {
                        point.delta = deltas[i];
                    }
                    newPoints.push(point);
                } else {
                    newPoints.push(null);
                }
            } else {
                removed++;
            }
        }
        this.points = newPoints;
        this.routeRemove();
    }

    public changePointsVisibility(visibility: boolean[]) {
        if (visibility.length == this.points.length) {
            let objectManager = this.map.getObjectManager();
            for (let i = 0; i < visibility.length; i++) {
                if (this.points[i] && this.currentPointsVisibility[i] != visibility[i]) {
                    if (visibility[i]) {
                        objectManager.show(this.points[i].mapObject.id);
                    } else {
                        objectManager.hide(this.points[i].mapObject.id);
                    }
                }
            }
            let routes = [this.route, this.baseRoute].filter(r => r);
            for (let route of routes) {
                if (route.polylines) {
                    for (let i = 0; i < route.polylines.length; i++) {
                        let segNo = route.polylines[i].segNum[0];
                        if (this.currentPointsVisibility[segNo] != visibility[segNo] || this.currentPointsVisibility[segNo+1] != visibility[segNo+1]) {
                            if (visibility[segNo] && visibility[segNo+1]) {
                                objectManager.show(route.polylines[i].id);
                            } else {
                                objectManager.hide(route.polylines[i].id);
                            }
                        }
                    }
                }
            }
        }
        this.currentPointsVisibility = visibility;
    }

    public showBalloonByJob(jobId: number) {
        let point = this.points.find(p => p && p.jobs?.indexOf(jobId) >= 0);
        if (point) {
            this.map.getObjectManager().showBalloon(point.mapObject.id);
        }
    }

    public routeRemove() {
        let routes = [this.route, this.baseRoute].filter(r => r);
        for (let route of routes) {
            if (route.polylines) {
                for (let i = 0; i < route.polylines.length; i++) {
                    this.map.UORemove(route.polylines[i].id);
                }
            }
        }
        this.route = null;
        this.baseRoute = null;
    }

    public routeSetId(id: string, baseId: string | null = null) {
        this.routeId = id;
        this.baseRouteId = baseId;
        this.routeRemove();
        this.routeGetData();
    }

    public destroy() {
        this.routeSetId('');
        this.map.getObjectManager().disableRefreshing('LastMile:Route');
        this.routeRemove();
        this.pointsRemove();
        this.map.getObjectManager().enableRefreshing('LastMile:Route');
        console.log('MapRoute, destroy');
    }
}
